import { useEffect } from 'react';
// rtl
import cssjanus from 'cssjanus';
import {
  COMMENT,
  DECLARATION,
  IMPORT,
  RULESET,
  KEYFRAMES,
  MEDIA,
  SUPPORTS,
  compile,
  serialize,
  stringify,
  strlen,
} from 'stylis';
import { prefixer } from 'stylis';
// emotion
import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
// @mui
import { useTheme } from '@mui/material/styles';

// ----------------------------------------------------------------------

type Props = {
  children: React.ReactNode;
};

type StylisElement = any;

type StylisPlugin = (element: StylisElement, index: number, children: StylisElement[]) => void;

const stringifyPreserveComments = (element: StylisElement, index: number, children: StylisElement[]) => {
  switch (element.type) {
    case IMPORT:
    case DECLARATION:
    case COMMENT:
      element.return = element.return || element.value;
      return element.return;
    case RULESET: {
      element.value = Array.isArray(element.props) ? element.props.join(',') : element.props;
      if (Array.isArray(element.children)) {
        element.children.forEach((child: StylisElement) => {
          if (child.type === COMMENT) {
            child.children = child.value;
          }
        });
      }
      break;
    }
    default:
      break;
  }

  const serializedChildren = serialize(([] as StylisElement[]).concat(element.children || []), stringify as any);

  if (!strlen(serializedChildren)) {
    return '';
  }

  element.return = `${element.value}{${serializedChildren}}`;
  return element.return;
};

const safeRtlPlugin: StylisPlugin = (element, index, children) => {
  if (
    element.type === KEYFRAMES ||
    element.type === SUPPORTS ||
    (element.type === RULESET && (!element.parent || element.parent.type === MEDIA || element.parent.type === RULESET))
  ) {
    try {
      const stringified = cssjanus.transform(stringifyPreserveComments(element, index, children));
      if (!stringified) {
        element.children = [];
        element.return = '';
        return;
      }

      const compiled = compile(stringified);
      const first = compiled && compiled.length > 0 ? compiled[0] : null;

      element.children = (first && Array.isArray(first.children) ? first.children : []) as StylisElement[];
      element.return = '';
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.error('stylis rtl plugin failed to process a rule, skipping it.', error);
      }
      element.children = element.children || [];
      element.return = '';
    }
  }
};

export default function ThemeRtlLayout({ children }: Props) {
  const theme = useTheme();

  useEffect(() => {
    document.dir = theme.direction;
  }, [theme.direction]);

  const cacheRtl = createCache({
    key: theme.direction === 'rtl' ? 'rtl' : 'css',
    stylisPlugins: theme.direction === 'rtl' ? [prefixer, safeRtlPlugin as any] : [],
  });

  return <CacheProvider value={cacheRtl}>{children}</CacheProvider>;
}
